import { cn } from '../../lib/utils';
import { UserPlus, Building2, DollarSign, Shield, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { services } from '../../services';

interface Activity {
  id: string;
  type: 'user' | 'asset' | 'transaction' | 'compliance';
  title: string;
  description: string;
  timestamp: string;
}

const iconMap = {
  user: UserPlus,
  asset: Building2,
  transaction: DollarSign,
  compliance: Shield,
};

const colorMap = {
  user: 'bg-info/20 text-info',
  asset: 'bg-primary/20 text-primary',
  transaction: 'bg-success/20 text-success',
  compliance: 'bg-warning/20 text-warning',
};

interface ActivityFeedProps {
  className?: string;
}

export function ActivityFeed({ className }: ActivityFeedProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await services.get<{
        status: boolean;
        message?: string;
        data: Array<{
          _id?: string;
          id?: string;
          type: string;
          title: string;
          description: string;
          createdAt?: string;
          timestamp?: string;
        }>;
      }>('/admin/dashboard/activities');

      if (response.status === 200 && response.data?.status && response.data?.data) {
        const formattedActivities = response.data.data.map((activity) => ({
          id: activity._id || activity.id || '',
          type: (activity.type || 'user') as Activity['type'],
          title: activity.title || 'Activity',
          description: activity.description || '',
          timestamp: formatTimestamp(
            activity.createdAt || activity.timestamp || new Date().toISOString()
          ),
        }));
        setActivities(formattedActivities);
      } else if (response.status === 401) {
        console.error('Authentication required for activity feed');
      } else if (response.status === 403) {
        console.error('Admin access required for activity feed');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 401) {
        console.error('Authentication required for activity feed');
      } else if (axiosError?.response?.status === 403) {
        console.error('Admin access required for activity feed');
      } else {
        console.error('Error fetching activity feed:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
    // Refresh activities every 60 seconds
    const interval = setInterval(fetchActivities, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTimestamp = (dateString: string): string => {
    const date = new Date(dateString);
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400)
      return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`;
    return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`;
  };

  return (
    <div className={cn('glass-card rounded-xl p-6 animate-slide-up', className)}>
      <div className='flex items-center justify-between mb-6'>
        <h3 className='text-lg font-semibold text-foreground'>Recent Activity</h3>
        <button className='flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors'>
          View all
          <ChevronRight className='w-4 h-4' />
        </button>
      </div>
      <div className='space-y-4'>
        {loading ? (
          <div className='p-8 text-center text-muted-foreground'>
            <p>Loading activities...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className='p-8 text-center text-muted-foreground'>
            <p>No recent activities</p>
          </div>
        ) : (
          activities.map((activity) => {
            const Icon = iconMap[activity.type];
            return (
              <div
                key={activity.id}
                className='flex items-start gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors cursor-pointer'
              >
                <div
                  className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-lg shrink-0',
                    colorMap[activity.type]
                  )}
                >
                  <Icon className='w-5 h-5' />
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium text-foreground'>{activity.title}</p>
                  <p className='text-xs text-muted-foreground truncate'>{activity.description}</p>
                </div>
                <span className='text-xs text-muted-foreground whitespace-nowrap'>
                  {activity.timestamp}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
